<template>
  <article class="report">
    <header class="report__header">
      <h3 class="section__subtitle">
        Exames e procedimentos
      </h3>
    </header>

    <div class="section__wrapper report__wrapper">
      <div
        v-if="filters.length > 0"
        class="report__filters"
      >
        <div
          v-for="(filter, index) in filters"
          :key="`report_filter_${index}`"
          class="report__filters-item"
        >
          <label class="report__filters-label">
            {{ filter.label }}
          </label>

          <template v-if="filter.type === 'date'">
            <div
              v-click-outside="() => filter.showCalendar = false"
              class="report__filters-field"
            >
              <div
                :class="filter.showCalendar && 'active'"
                class="report__filters-date"
                @click="filter.showCalendar = !filter.showCalendar"
              >
                <span>
                  {{ $moment(filter.value).format('DD/MM/YYYY') }}
                </span>

                <dr-icon
                  :color="'#8E8E92'"
                  class="agenda-settings__date-picker--icon"
                  name="dashCalendar"
                />
              </div>

              <div class="report__filters-calendar">
                <dr-calendar
                  v-show="filter.showCalendar"
                  v-model="filter.value"
                  @change="filter.showCalendar = false"
                />
              </div>
            </div>
          </template>

          <template v-if="filter.type === 'text'">
            <div class="report__filters-field">
              <input
                v-model.lazy="filter.value"
                :placeholder="filter.placeholder"
                type="text"
                class="report__filters-text"
              >

              <i
                v-show="filter.value"
                class="icon icon-cross report__filters-reset"
                @click="filter.value = null"
              />
            </div>
          </template>
        </div>
      </div>

      <div class="report__body">
        <dr-loader
          v-if="ui.isLoading"
          size="lg"
          class="report__loader"
        />

        <p
          v-if="!ui.isLoading && Array.isArray(data.rows) && data.rows.length === 0"
          class="report__no-results"
        >
          Nenhum resultado encontrado.
        </p>

        <template
          v-if="!ui.isLoading && Array.isArray(data.rows) && data.rows.length > 0"
          tag="div"
          class="report__table"
        >
          <div class="report__table-header">
            <div
              v-for="(key, index) in data.keys"
              :key="`report_header_item_${index}`"
              :class="key.class"
              class="report__table-col"
            >
              {{ key.title }}
            </div>
          </div>

          <div
            v-for="(row, index) in data.rows"
            :key="`report_table_row_${index}`"
            class="report__table-row"
          >
            <div
              v-for="(cell, i) in row"
              :key="`report_table_row_${index}_cell_${i}`"
              :class="cell.class"
              class="report__table-cell"
            >
              {{ cell.value }}
            </div>
          </div>
        </template>
      </div>
    </div>

    <footer
      v-if="ui.hasLoaded && pagination.totalPages > 1"
      class="section__footer"
    >
      <nav class="pagination">
        <transition name="fade" mode="out-in">
          <dr-table-pagination
            :total-pages.sync="pagination.totalPages"
            :current-page.sync="pagination.currentPage"
            class="text-right"
            @updated="getReport($event)"
          />
        </transition>
      </nav>
    </footer>
  </article>
</template>

<script>
import DrTablePagination from '@/components/tables/tablePagination'
import { DrCalendar } from 'cm2tech-vue-calendar'
import { mapGetters } from 'vuex'

export default {
  name: 'DrReportsExamsProcedures',

  components: {
    DrTablePagination,
    DrCalendar
  },

  data () {
    return {
      data: {
        keys: [
          {
            title: 'Nome',
            class: 'report__table-col--80 mobile:w-8/12'
          },
          {
            title: 'Quantidade',
            class: 'report__table-col--20 mobile:w-4/12'
          }
        ],
        rows: null
      },
      filters: [
        {
          name: 'startDate',
          label: 'Data inicial',
          type: 'date',
          queryString: '&filterAndGreaterThanOrEqual[a.appointmentStart]=',
          value: this.$moment().startOf('month').toISOString(),
          showCalendar: false
        },
        {
          name: 'endDate',
          label: 'Data final',
          type: 'date',
          queryString: '&filterAndLessThanOrEqual[a.appointmentFinish]=',
          value: null,
          showCalendar: false
        },
        {
          name: 'name',
          label: 'Filtrar por nome',
          placeholder: 'Digite o nome',
          type: 'text',
          queryString: '&filterAndLike[ms.name]=',
          value: null
        }
      ],
      pagination: {
        totalRows: null,
        totalPages: null,
        currentPage: null
      },
      itemsPerPage: 10,
      ui: {
        isLoading: true,
        hasLoaded: false
      }
    }
  },

  computed: {
    ...mapGetters({
      office: 'getCurrentOffice'
    }),

    endpoint () {
      return `/report/doctor_office/${this.office.id}/medical_service`
    },

    queryString () {
      return this.filters.reduce((prev, next, i) => {
        const value = next.type === 'date'
          ? this.$moment(next.value).format('YYYY-MM-DD')
          : next.value

        return next.value
          ? `${prev}${next.queryString}${value}`
          : prev
      }, '')
    }
  },

  watch: {
    filters: {
      deep: true,
      immediate: false,
      handler (next, prev) {
        const startDate = next.find(f => f.name === 'startDate')
        const endDate = next.find(f => f.name === 'endDate')

        if (this.$moment(startDate.value).isAfter(this.$moment(endDate.value), 'day')) {
          endDate.value = startDate.value
        }
      }
    },

    queryString: {
      deep: false,
      immediate: false,
      handler (next, prev) {
        if (prev !== next) {
          this.getReport()
        }
      }
    },

    'office.id': {
      deep: false,
      immediate: false,
      handler () {
        this.getReport()
      }
    }
  },

  created () {
    this.getReport()
  },

  methods: {
    getReport (page = 1) {
      const url = `${this.endpoint}?itensPerPage=${this.itemsPerPage}${this.queryString}&page=${page}`

      this.ui.isLoading = true
      this.fn_SHOW_PROGRESS()
      this.$http
        .get(url, this.$store.getters.getAuthHeader)
        .then((res) => {
          const data = res.data.data
            .map(item => ({ name: item.name, total: item.total }))
            .map(item => Object.entries(item))
            .map(item => {
              return item.map((cell, index) => ({
                value: cell[1],
                class: index === 0 ? 'report__table-cell--80 mobile:w-8/12' : 'report__table-cell--20 mobile:w-4/12'
              }))
            })

          this.data.rows = data
        })
        .finally(() => {
          this.ui.isLoading = false
          this.ui.hasLoaded = true
          this.fn_HIDE_PROGRESS()
        })
    }
  }
}
</script>
